import { AutoComplete, Button, Card, Input, Modal, Select } from "antd";
import { Content } from "antd/es/layout/layout";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  createSingleAssistTecTicket,
  createSingleTicket,
} from "../../utils/helpers/ticketHelpers";
import { useSelector } from "react-redux";
import { AuthenticationState } from "../../interfaces/users";
import { toast } from "react-toastify";
import _ from "lodash";
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
} from "react-query";
import { api } from "../../config";
import TextArea from "antd/es/input/TextArea";
import { ImportClientByDocumentModal } from "./ImportClientByDocument";
import { useModal } from "../../utils/hooks/useModal";
import { uploadSingleFile } from "../../utils/helpers/attachmentHelpers";
import { ReferenceType } from "../../interfaces";
import {
  ModulePermission,
  userHasPermission,
} from "../../utils/helpers/userHelpers";

interface CreateSingleTicketModalProps {
  type: "checklist" | "measuring" | "assemble";
  onClose: () => void;
  isOpen: boolean;
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<any, unknown>>;
}

export const formatTaxId = (value: string) => {
  try {
    if (value.length === 14) {
      return value.replace(
        /(\d{3})(\d{3})(\d{3})(\d{4})(\d{2})/g,
        "$1.$2.$3/$4-$5"
      );
    } else {
      return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "$1.$2.$3-$4");
    }
  } catch (error) {
    toast.error("Ocorreu um erro ao formatar o CPF/CNPJ");
  }
};

export const onChangeCurrencyInput = (
  value: string,
  setValue?: Dispatch<SetStateAction<string>>,
  length = 5
) => {
  const onlyNumbers = value.replace(/\D+/g, "");
  if (onlyNumbers.length > length)   return onlyNumbers.substring(0, length).replace(/(\d{0,4})(\d{2})/, "$1,$2");
  setValue?.(onlyNumbers.replace(/(\d{0,4})(\d{2})/, "$1,$2"));
  return onlyNumbers.replace(/(\d{0,4})(\d{2})/, "$1,$2");
};

export const CreateSingleTicketModal = ({
  type,
  onClose,
  isOpen,
  refetch,
}: CreateSingleTicketModalProps) => {
  const [loading, setLoading] = useState(false);
  const { user, token } = useSelector((state: AuthenticationState) => state);
  const [clients, setClients] = useState<any[]>([]);
  const [typing, setTyping] = useState(false);
  const [lastTimeout, setLastTimeout] = useState<any | undefined>(undefined);
  const [clientField, setClientField] = useState("");
  const [clientLoading, setClientLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [priority, setPriority] = useState(2);
  const [priorityReason, setPriorityReason] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(
    undefined
  );
  const [ticketType, setTicketType] = useState<string>("ASSISTENCIA");
  const [ticketValue, setTicketValue] = useState<string>("");
  const { isOpen: isOpenImport, onOpen, onClose: onCloseImport } = useModal();

  const handleClose = () => {
    setClientField("");
    setNotes("");
    setSelectedClientId(undefined);
    setTicketValue("");
    setTicketType("ASSISTENCIA");
    setIdAssistTec("");
    setPriority(2);
    setPriorityReason("");
    onClose();
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!ticketType || !ticketValue || !notes)
      return toast.error("Todos os campos são obrigatórios");

    if (ticketType === "ASSISTENCIA") {
      if (!technicalAssistanceProducts.length) {
        return toast.error("É necessário adicionar ao menos 1 produto");
      }
      if (!idAssistTec) {
        return toast.error("O ID da Assistência Técnica é obrigatório");
      }

      if (!saleId) {
        return toast.error("O ID da Venda de Origem é obrigatório");
      }

      if (
        !!technicalAssistanceProducts.find((item) => !item.name) ||
        !!technicalAssistanceProducts.find((item) => !item.reason) ||
        !!technicalAssistanceProducts.find((item) => !item.partNumber)
      ) {
        return toast.error(
          "Todos os campos dos produtos precisam ser preenchidos"
        );
      }
      try {
        setLoading(true);
        const response = await createSingleAssistTecTicket(token, {
          fieldReport: {
            clientId: selectedClientId,
            value: ticketValue,
            notes: notes,
            saleId: Number(saleId),
            idAssistTec: Number(idAssistTec),
            priority,
            priorityReason,
          },
          products: technicalAssistanceProducts,
        });
        setTechnicalAssistanceProducts([]);
      } catch (error: any) {
        return toast.error(error.response?.data?.error);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(true);
      await createSingleTicket(token, {
        clientId: selectedClientId,
        type: ticketType,
        value: ticketValue,
        notes: notes,
        priority,
        priorityReason,
      });
    }
    refetch();
    setLoading(false);
    handleClose();
  };

  const fetchClients = useCallback(async () => {
    if (clientField.length < 4) return;
    try {
      setClientLoading(true);
      const resultClients = await api(token).get(
        `clients/search/?searchString=${clientField}`
      );
      setClients(resultClients);
    } catch (error) {
      toast.error("Ocorreu um erro");
    } finally {
      setClientLoading(false);
    }
  }, [clientField, token]);

  const onChangeInput = (string: string) => {
    if (lastTimeout) clearTimeout(lastTimeout);
    setClientLoading(true);
    setTyping(true);
    const timeout = setTimeout(() => setTyping(false), 800);
    setLastTimeout(timeout);
    setClientField(string);
  };
  useEffect(() => {
    if (typing) {
      return;
    } else {
      fetchClients();
    }
  }, [fetchClients, typing]);

  const [technicalAssistanceProducts, setTechnicalAssistanceProducts] =
    useState<
      {
        name: string;
        reason: string;
        partNumber: string;
        issueImages: { attachmentId: number; url: string }[];
      }[]
    >([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [uploadLoading, setUploadLoading] = useState(false);

  const [idAssistTec, setIdAssistTec] = useState<string | undefined>();
  const [saleId, setSaleId] = useState<string | undefined>();

  const fileInputRef = useRef<any>(undefined);

  const handleAddProduct = () => {
    setTechnicalAssistanceProducts((prev) => [
      ...prev,
      {
        issueImages: [],
        name: "",
        partNumber: "",
        reason: "",
      },
    ]);
  };

  const handleRemoveProduct = (index: number) => {
    setTechnicalAssistanceProducts((prev) =>
      prev.filter((prod, idx) => idx !== index)
    );
  };

  const handleOpenFileSelector = (index: number) => {
    setCurrentImageIndex(index);
    fileInputRef.current?.click?.();
  };

  const handleChangeNestedInput = (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { value, name } = e.target;

    const tempForm = [...technicalAssistanceProducts];

    tempForm.splice(index, 1, { ...tempForm[index], [name]: value });
    setTechnicalAssistanceProducts(tempForm);
  };

  const handleUploadImages = async (e: ChangeEvent<HTMLInputElement>) => {
    const requests: Promise<any>[] = [];
    setUploadLoading(true);
    Array.from(e.target?.files ?? []).forEach((_, index) => {
      if (e.target.files?.[index]) {
        requests.push(
          uploadSingleFile(
            e.target.files[index],
            undefined,
            ReferenceType.TICKET,
            token,
            undefined,
            undefined
          )
        );
      }
    });
    const files = await Promise.all(requests);
    setUploadLoading(false);
    let products = [...technicalAssistanceProducts];

    products[currentImageIndex] = {
      ...products[currentImageIndex],
      issueImages: [
        ...(products[currentImageIndex].issueImages ?? []),
        ...files.map((file) => ({ attachmentId: file.id, url: file.url })),
      ],
    };

    setTechnicalAssistanceProducts(products);
  };

  const handleDeleteAttachment = async (
    attachmentId: number,
    index: number
  ) => {
    try {
      setUploadLoading(true);
      await api(token).delete(`/attachment/${attachmentId}`);
      let products = [...technicalAssistanceProducts];

      products[index] = {
        ...products[index],
        issueImages: [
          ...(products[index].issueImages?.filter(
            (image) => image.attachmentId !== attachmentId
          ) ?? []),
        ],
      };

      setTechnicalAssistanceProducts(products);
    } catch (error) {
      toast.error("Ocorreu um erro ao deletar o arquivo");
    } finally {
      setUploadLoading(false);
    }
  };

  const [importLoading, setImportLoading] = useState(false);

  const handleGetProducts = async () => {
    setImportLoading(true);
    try {
      const products = await api(token).get(
        `/tickets/assist-products?idAssistTec=${idAssistTec}`
      );
      if (!products.length){
        return toast.error("Nenhuma informação encontrada para a Assistencia Técnica informada.")
      }
      setTechnicalAssistanceProducts((prev) => [...products.map((product: any) => ({name: product.Nome, reason: '', partNumber: product.Ref, issueImages: []}))])
      setClientField(products[0].client.name)
      setSelectedClientId(products[0].client.id)
      setNotes(products?.[0]?.Descri)
    } catch (error) {
      toast.error("Ocorreu um erro ao importar Assistencia Técnica");
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <>
      <ImportClientByDocumentModal
        isOpen={isOpenImport}
        onClose={onCloseImport}
      />
      <Modal
        open={isOpen}
        confirmLoading={loading}
        onCancel={handleClose}
        onOk={handleSubmit}
      >
        <Content>
          <h2>Criar Ticket de Montagem Avulso</h2>
          <Button variant="solid" color="blue" onClick={onOpen}>
            Importar Cliente
          </Button>
          <br />
          <br />
          <label className="text-base text-[#8C8C8C]">
            Cliente<span className="text-red-600">*</span>:
          </label>
          <div className="flex flex-col gap-8">
            <div className="relative">
              {clientLoading && (
                <p className="absolute top-0 right-2 z-50">Carregando...</p>
              )}
              <AutoComplete
                //@ts-ignore
                value={clientField}
                onChange={(e) => onChangeInput(e)}
                onSelect={(e) => {
                  const client = JSON.parse(e);
                  setClientField(client.name);
                  setSelectedClientId(client.id);
                }}
                allowClear
                options={
                  clients?.map?.((client: any) => ({
                    label: `${client.name}---CPF/CNPJ: ${formatTaxId(
                      client.taxId
                    )}---${client.deliveryAddress.street}`,
                    value: JSON.stringify(client),
                  })) ?? []
                }
                size="large"
                style={{ width: "100%" }}
                optionRender={(props) => {
                  const splitted = String(props.label)?.split?.("---");

                  return (
                    <div>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {splitted[0]}
                      </div>
                      <div>{splitted[1]}</div>
                      <div>{splitted[2]}</div>
                    </div>
                  );
                }}
              />
            </div>
            <div className="w-full flex flex-col">
              <label className="text-base text-[#8C8C8C]">
                Tipo<span className="text-red-600">*</span>:
              </label>
              <Select
                size="large"
                value={ticketType}
                onChange={(e) => setTicketType(e)}
              >
                <Select.Option value="ASSISTENCIA">Assistência</Select.Option>
                <Select.Option value="ORDEM DE SERVICO">
                  Ordem de Serviço
                </Select.Option>
                <Select.Option value="VISTORIA">Vistoria</Select.Option>
                <Select.Option value="MONTAGEM">Montagem</Select.Option>
                <Select.Option value="REGULAGEM">Regulagem</Select.Option>
                <Select.Option value="FRETE">Frete</Select.Option>
                <Select.Option value="QUILOMETRAGEM">
                  Quilometragem
                </Select.Option>
                <Select.Option value="RECOLHA">Recolha</Select.Option>
                <Select.Option value="REEMBOLSO">Reembolso</Select.Option>
              </Select>
            </div>

            {ticketType === "ASSISTENCIA" && (
              <div>
                <div className="flex flex-col gap-2 mb-2">
                  <label>
                    ID Assistencia Técnica
                    <span className="text-red-600">*</span>:{" "}
                  </label>
                  <Input
                    onChange={(e) =>
                      setIdAssistTec(e.target.value.replace(/\D+/gi, ""))
                    }
                    value={idAssistTec}
                  />
                  <Button
                    onClick={handleGetProducts}
                    loading={importLoading}
                    variant="solid"
                    color="primary"
                  >
                    Importar Assistência Técnica
                  </Button>
                </div>

                <Card
                  title={
                    <div className="w-full flex justify-between items-center">
                      <p>Produtos</p>{" "}
                      <Button onClick={handleAddProduct}>+</Button>
                    </div>
                  }
                >
                  <div className="flex flex-col gap-10">
                    {technicalAssistanceProducts.map((product, index) => (
                      <div
                        className="border-blue-600 border-2 rounded-md shadow p-3 flex flex-col gap-3"
                        key={`assist-product-${index}`}
                      >
                        <div>
                          <label>
                            Nome do Produto
                            <span className="text-red-600">*</span>:
                          </label>
                          <Input
                            onChange={(e) => handleChangeNestedInput(e, index)}
                            name="name"
                            value={technicalAssistanceProducts[index].name}
                            type="text"
                          />
                        </div>
                        <div>
                          <label>
                            Motivo<span className="text-red-600">*</span>:
                          </label>
                          <Input
                            onChange={(e) => handleChangeNestedInput(e, index)}
                            name="reason"
                            value={technicalAssistanceProducts[index].reason}
                            type="text"
                          />
                        </div>
                        <div>
                          <label>
                            Numero da peça
                            <span className="text-red-600">*</span>:
                          </label>
                          <Input
                            onChange={(e) => handleChangeNestedInput(e, index)}
                            name="partNumber"
                            value={
                              technicalAssistanceProducts[index].partNumber
                            }
                            type="text"
                          />
                        </div>
                        <Card
                          title={
                            <div className="w-full flex justify-between items-center">
                              <p>Imagens</p>
                              <Button
                                onClick={() => handleOpenFileSelector(index)}
                                loading={uploadLoading}
                              >
                                +
                              </Button>
                            </div>
                          }
                        >
                          <div className="flex flex-col gap-4 items-center">
                            {product.issueImages.map((image) => (
                              <div className="p-4 shadow flex flex-col items-center gap-2">
                                <img
                                  className="w-3/4"
                                  key={image.attachmentId}
                                  src={image.url}
                                  alt="img"
                                />
                                <Button
                                  onClick={() =>
                                    handleDeleteAttachment(
                                      image.attachmentId,
                                      index
                                    )
                                  }
                                >
                                  Remover
                                </Button>
                              </div>
                            ))}
                          </div>
                        </Card>
                        <div>
                          <Button onClick={() => handleRemoveProduct(index)}>
                            Remover
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
                <input
                  onChange={(e) => handleUploadImages(e)}
                  hidden
                  type="file"
                  multiple
                  ref={fileInputRef}
                />

                <label>
                  ID Venda de Origem<span className="text-red-600">*</span>
                  :{" "}
                </label>
                <Input
                  onChange={(e) =>
                    setSaleId(e.target.value.replace(/\D+/gi, ""))
                  }
                  value={saleId}
                />
              </div>
            )}

            <div>
              <label className="text-base text-[#8C8C8C]">
                Valor<span className="text-red-600">*</span>:
              </label>
              <Input
                onChange={(e) =>
                  onChangeCurrencyInput(e.target.value, setTicketValue)
                }
                value={ticketValue}
              />
            </div>
            <div>
              <label className="text-base text-[#8C8C8C]">
                Observação<span className="text-red-600">*</span>:
              </label>
              <TextArea
                rows={3}
                onChange={(e) => setNotes(e.target.value)}
                value={notes}
              />
            </div>
            <div>
              <label className="text-base text-[#8C8C8C]">Prioridade:</label>
              <Select
                onChange={(e) => setPriority(e)}
                disabled={
                  !userHasPermission(user, {
                    management: ModulePermission.ADMIN,
                  })
                }
                style={{ width: "100%" }}
                value={priority}
              >
                <Select.Option value={2}>Normal</Select.Option>
                <Select.Option value={1}>Alta</Select.Option>
              </Select>
              {priority === 1 && (
                <>
                  <label className="text-base text-[#8C8C8C]">
                    Razão Prioridade:
                  </label>
                  <TextArea
                    rows={3}
                    onChange={(e) => setPriorityReason(e.target.value)}
                    value={priorityReason}
                  />
                </>
              )}
            </div>
          </div>
        </Content>
      </Modal>
    </>
  );
};
